import { useState, useEffect } from 'preact/hooks';
import Router from 'preact-router';
import { AuthProvider, useAuth } from './lib/auth';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { TrialBanner } from './components/TrialBanner';
import { Dashboard } from './pages/Dashboard';
import { TeamPage } from './pages/Team';
import { RequestsPage } from './pages/Requests';
import { CalendarPage } from './pages/Calendar';
import { WallchartPage } from './pages/Wallchart';
import { SettingsPage } from './pages/Settings';
import { LoginPage } from './pages/Login';
import { SetupPage } from './pages/Setup';
import { SubscriptionExpired } from './pages/SubscriptionExpired';
import { SubscriptionSuccess } from './pages/SubscriptionSuccess';
import { SubscriptionCancelled } from './pages/SubscriptionCancelled';
import { UpgradePage } from './pages/Upgrade';
import { BillingPage } from './pages/Billing';
import { SuperAdminDashboard } from './pages/super-admin/Dashboard';
import { SuperAdminWorkspaces } from './pages/super-admin/Workspaces';
import { SuperAdminWorkspaceDetail } from './pages/super-admin/WorkspaceDetail';
import { SupportButton } from './components/SupportButton';

function AppContent() {
  const { user, workspace, subscription, loading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');

  // Handle route changes
  function handleRouteChange(e) {
    setCurrentPath(e.url || '/');
  }

  if (loading) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Check if we're on setup page
  if (typeof window !== 'undefined' && window.location.pathname === '/setup') {
    return <SetupPage />;
  }

  // Check if we're on login page with a token (seamless Slack login)
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/login' && params.get('token')) {
      return <LoginPage />;
    }
  }

  // Subscription result pages (no layout needed)
  if (typeof window !== 'undefined') {
    if (window.location.pathname === '/subscription/success') {
      return <SubscriptionSuccess />;
    }
    if (window.location.pathname === '/subscription/cancelled') {
      return <SubscriptionCancelled />;
    }
  }

  // Super Admin pages (protected by Cloudflare Access)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/super-admin')) {
    return (
      <div class="super-admin-layout">
        <Router>
          <SuperAdminDashboard path="/super-admin" />
          <SuperAdminWorkspaces path="/super-admin/workspaces" />
          <SuperAdminWorkspaceDetail path="/super-admin/workspaces/:id" />
        </Router>
      </div>
    );
  }

  if (!user || !workspace) {
    return <LoginPage />;
  }

  // Check subscription status - redirect to expired page if not valid
  if (subscription && !subscription.is_valid) {
    return <SubscriptionExpired subscription={subscription} workspace={workspace} />;
  }

  return (
    <div class="app-layout">
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        workspace={workspace}
        currentPath={currentPath}
      />
      <div class="main-content">
        {/* Show trial banner if in trial */}
        <TrialBanner subscription={subscription} />
        <Header
          user={user}
          workspace={workspace}
          onMenuClick={() => setSidebarOpen(true)}
          onLogout={logout}
        />
        <main class="page-content">
          <Router onChange={handleRouteChange}>
            <Dashboard path="/" user={user} workspace={workspace} />
            <WallchartPage path="/wallchart" user={user} workspace={workspace} />
            <TeamPage path="/team" user={user} workspace={workspace} />
            <RequestsPage path="/requests" user={user} workspace={workspace} />
            <CalendarPage path="/calendar" user={user} workspace={workspace} />
            <SettingsPage path="/settings" user={user} workspace={workspace} />
            <UpgradePage path="/upgrade" />
            <BillingPage path="/billing" />
          </Router>
        </main>
      </div>
      {/* Support button */}
      <SupportButton />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
